<!--
****** NOTES: ******
- keyboard navigation (using span as a wrapper... use a button and reset it. default behaviour for keyboard nav)
-->

<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <span
    :class="chipClasses"
    data-samora
    tabindex="-1"
    v-on="disabled || !interactive ? {} : $listeners"
  >
    <!-- @slot Use this slot to prepend content to the Chip -->
    <slot name="prepend" />
    <span class="sam-chip__label">
      <template v-if="!$slots.default">
        {{ label }}
      </template>
      <slot />
    </span>
    <!-- @slot Use this slot to append content to the Chip -->
    <slot name="append" />
  </span>
</template>

<script>
import SAMORA_TYPES from '../../../utils/constants/samora-types.enum';

const { CHIP: SAMORA_CHIP_TYPES } = SAMORA_TYPES;

export const CHIP_TYPES = SAMORA_CHIP_TYPES;

export default {
  name: 'SamChip',
  props: {
    /**
     * The Chip label
     */
    label: {
      type: [Number, String],
      required: true,
      default: '',
    },
    /**
     * The Samora chip type to be applied
     * @values default, outlined
     */
    samType: {
      type: String,
      required: false,
      default: CHIP_TYPES.DEFAULT,
      validator: (val) => Object.values(CHIP_TYPES).includes(val),
    },
    /**
     * The Chip active state
     * @values true, false
     */
    active: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Chip is disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    interactive: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    chipClasses() {
      return [
        'sam-chip',
        `is-${this.samType}`,
        {
          'is-active': this.active,
          'is-disabled': this.disabled,
          'is-static': !this.interactive,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-chip {
  @extend .text--body-2, .d-inline-flex, .flex-center, .center-xs, .px-3, .py-1;
  border: $size--0--fourth solid $informative--surface;
  border-radius: $size--7-half;
  background-color: $informative--surface;
  color: $pure--black;
  white-space: nowrap;

  &:not(.is-static) {
    cursor: pointer;
  }

  &__label {
    text-decoration: none;
    /* text unselectable */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:not(:first-child) {
      @extend .pl-1;
    }

    &:not(:last-child) {
      @extend .pr-1;
    }
  }

  &.is-active {
    @extend .text--body-2--medium;
    border-color: $informative--main;
    background-color: $informative--surface;
    color: $informative--pressed;
  }

  &:hover:not(.is-static) {
    border-color: $informative--hover;
    color: $informative--hover;
  }

  &:focus:not(.is-static) {
    border-color: $informative--hover;
    outline: 3px solid $informative--border;
    color: $informative--hover;
  }

  &.is-outlined {
    background-color: transparent;
    border-color: $neutral--40;

    &.is-active {
      border-color: $informative--main;
      background-color: $informative--surface;
      color: $informative--main;
    }

    &:hover:not(.is-static) {
      border-color: $informative--hover;
      color: $informative--hover;
    }

    &:focus:not(.is-static) {
      border-color: $informative--hover;
      background-color: $informative--surface;
      outline: 3px solid $informative--border;
      color: $informative--hover;
    }
  }

  &.is-disabled:not(.is-static) {
    border-color: $neutral--40;
    background-color: $neutral--20;
    cursor: not-allowed;
    pointer-events: none;
    color: $neutral--60;
  }
}
</style>
